/**
 * Plausibly shaped honeytoken values (ADR-0011): each generator draws from
 * a seeded PRNG and produces a value that passes the format checks a script
 * might run (JWT structure, UUID version bits, `_ga` layout, Luhn).
 */
import { nextRandom, type RandomState, seedRandom } from "../random";
import { base64, utf8Bytes } from "./encode";

/** A seeded random source with helpers (mutable; local to one generation). */
export class Rng {
  #state: RandomState;

  constructor(seed: number) {
    this.#state = seedRandom(seed);
  }

  /** A float in [0, 1). */
  next(): number {
    const [value, state] = nextRandom(this.#state);
    this.#state = state;
    return value;
  }

  /** An integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.next() * n);
  }

  pick<T>(items: readonly T[]): T {
    return items[this.int(items.length)] as T;
  }

  /** `n` characters drawn from `alphabet`. */
  chars(alphabet: string, n: number): string {
    let out = "";
    for (let i = 0; i < n; i++) out += alphabet[this.int(alphabet.length)];
    return out;
  }
}

/** FNV-1a over `text`: stable, well-mixed seeds for per-source generators. */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export const DIGITS = "0123456789";
export const LOWER = "abcdefghijklmnopqrstuvwxyz";
export const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const HEX = "0123456789abcdef";
const BASE64URL = `${UPPER}${LOWER}${DIGITS}-_`;

export function uuid(rng: Rng): string {
  const variant = rng.pick(["8", "9", "a", "b"]);
  return `${rng.chars(HEX, 8)}-${rng.chars(HEX, 4)}-4${rng.chars(HEX, 3)}-${variant}${rng.chars(HEX, 3)}-${rng.chars(HEX, 12)}`;
}

const DAY_S = 86_400;

/** A Google Analytics `_ga` cookie: `GA1.1.<random 10 digits>.<first-visit seconds>`. */
export function gaCookie(rng: Rng, now: number, prefix = "GA1.1"): string {
  const id = `${rng.chars("123456789", 1)}${rng.chars(DIGITS, 9)}`;
  const nowS = Math.floor(now / 1000);
  const visited = nowS - rng.int(365 * DAY_S);
  const stamp = visited >= 1e9 ? String(visited) : `1${rng.chars(DIGITS, 9)}`;
  return `${prefix}.${id}.${stamp}`;
}

const segment = (json: unknown) => base64(utf8Bytes(JSON.stringify(json)), true);

/** An HS256 JWT with a plausible subject and lifetime, and a random signature. */
export function jwt(rng: Rng, now: number): string {
  const iat = Math.floor(now / 1000) - rng.int(DAY_S);
  const claims = { sub: `u_${10_000 + rng.int(90_000)}`, iat, exp: iat + DAY_S };
  return `${segment({ alg: "HS256", typ: "JWT" })}.${segment(claims)}.${rng.chars(BASE64URL, 43)}`;
}

/** A 16-digit Luhn-valid Visa-shaped number. */
export function cardNumber(rng: Rng): string {
  const body = `4${rng.chars(DIGITS, 14)}`;
  let sum = 0;
  // The check digit will sit to the right, so double from the rightmost body digit.
  for (let i = 0; i < body.length; i++) {
    let d = Number(body[body.length - 1 - i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return `${body}${(10 - (sum % 10)) % 10}`;
}

/** `MM/YY`, one to five years after `now`. */
export function cardExpiry(rng: Rng, now: number): string {
  const year = (new Date(now).getUTCFullYear() + 1 + rng.int(5)) % 100;
  const month = 1 + rng.int(12);
  return `${String(month).padStart(2, "0")}/${String(year).padStart(2, "0")}`;
}

export const cardCvc = (rng: Rng): string => rng.chars(DIGITS, 3);

/** A US-style number in the 555-01xx range reserved for fiction. */
export const phone = (rng: Rng): string =>
  `(${rng.chars("23456789", 1)}${rng.chars(DIGITS, 2)}) 555-01${rng.chars(DIGITS, 2)}`;

/** A password with every character class a strength check asks for. */
export function password(rng: Rng): string {
  const parts = [
    rng.chars(UPPER, 3),
    rng.chars(LOWER, 5),
    rng.chars(DIGITS, 3),
    rng.chars("!@#$%*", 1),
    rng.chars(LOWER, 2),
  ];
  return parts.join("");
}

const FIRST = [
  "jordan",
  "avery",
  "morgan",
  "riley",
  "casey",
  "quinn",
  "harper",
  "rowan",
  "emerson",
  "sawyer",
  "kendall",
  "reese",
  "dakota",
  "marlow",
  "sasha",
  "elliot",
];
const LAST = [
  "lee",
  "nakamura",
  "okafor",
  "lindqvist",
  "moreau",
  "castillo",
  "brennan",
  "haddad",
  "kowalski",
  "fairbanks",
  "delgado",
  "whitlock",
  "abernathy",
  "petrov",
  "achebe",
  "sorensen",
];

/** The run's simulated user: one name behind the email and the card. */
export interface Persona {
  first: string;
  last: string;
  /** Four random characters that make the email address unique. */
  tag: string;
}

export const persona = (rng: Rng): Persona => ({
  first: rng.pick(FIRST),
  last: rng.pick(LAST),
  tag: rng.chars(`${LOWER}${DIGITS}`, 4),
});

const title = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;

export const fullName = (p: Persona): string => `${title(p.first)} ${title(p.last)}`;

export const email = (p: Persona, domain = "example.com"): string =>
  `${p.first}.${p.last}.${p.tag}@${domain}`;

/**
 * A value with the same layout as `value`: separators and short words are
 * kept, and each character of a longer run is redrawn from its class
 * (digits, lowercase, uppercase; hex runs stay hex). Returns the value and
 * how many characters were redrawn.
 */
export function reshape(rng: Rng, value: string): { value: string; random: number } {
  let out = "";
  let random = 0;
  for (const run of value.match(/[A-Za-z0-9]+|[^A-Za-z0-9]+/g) ?? []) {
    if (!/^[A-Za-z0-9]/.test(run) || run.length < 6) {
      out += run;
      continue;
    }
    random += run.length;
    if (/^[0-9a-f]+$/.test(run) && /[a-f]/.test(run)) {
      out += rng.chars(HEX, run.length);
      continue;
    }
    if (/^[0-9A-F]+$/.test(run) && /[A-F]/.test(run)) {
      out += rng.chars(HEX.toUpperCase(), run.length);
      continue;
    }
    for (const ch of run) {
      out += rng.chars(/\d/.test(ch) ? DIGITS : /[a-z]/.test(ch) ? LOWER : UPPER, 1);
    }
  }
  return { value: out, random };
}
